/**
 * Unified AdviceState — keeps news, NBA, cashflow, vision, and dashboard in sync.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';

const AdviceSyncContext = createContext(null);

export function AdviceSyncProvider({ children, user }) {
  const [state, setState] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);

  const load = useCallback(async (refresh = false) => {
    if (!user?.user_id) {
      setState(null);
      setRecommendations([]);
      return;
    }
    setLoading(true);
    try {
      const [stateRes, recRes] = await Promise.all([
        axios.get('/api/advice/state', { params: refresh ? { refresh: true } : {} }),
        axios.get('/api/advice/recommendations'),
      ]);
      setState(stateRes.data?.advice_state || null);
      setRecommendations(recRes.data?.recommendations || []);
      setLastSynced(new Date().toISOString());
    } catch (err) {
      console.error('[AdviceSync] load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.user_id]);

  useEffect(() => {
    load(false);
  }, [load]);

  const refreshAfterChat = useCallback(async () => {
    try {
      await axios.post('/api/advice/sync/refresh');
    } catch {
      /* optional */
    }
    await load(false);
  }, [load]);

  const applySyncPayload = useCallback((sync) => {
    if (!sync) return;
    setState((prev) => ({
      ...(prev || {}),
      whats_most_important_now: sync.whats_most_important_now || prev?.whats_most_important_now,
      priorities: sync.priorities || prev?.priorities,
      canonical_recommendation_id:
        sync.canonical_recommendation_id || prev?.canonical_recommendation_id,
    }));
    setLastSynced(new Date().toISOString());
  }, []);

  const value = useMemo(
    () => ({
      state,
      recommendations,
      loading,
      lastSynced,
      focusLine:
        state?.whats_most_important_now ||
        (state?.priorities?.length ? `Focus on: ${state.priorities[0]}` : ''),
      keyFacts: state?.key_facts || {},
      refresh: () => load(true),
      reload: () => load(false),
      refreshAfterChat,
      applySyncPayload,
    }),
    [state, recommendations, loading, lastSynced, load, refreshAfterChat, applySyncPayload],
  );

  return <AdviceSyncContext.Provider value={value}>{children}</AdviceSyncContext.Provider>;
}

export function useAdviceSync() {
  const ctx = useContext(AdviceSyncContext);
  if (!ctx) {
    return {
      state: null,
      recommendations: [],
      loading: false,
      focusLine: '',
      keyFacts: {},
      refresh: async () => {},
      reload: async () => {},
      refreshAfterChat: async () => {},
      applySyncPayload: () => {},
    };
  }
  return ctx;
}

export default AdviceSyncContext;
